import React, { useState, useEffect, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import api from '../utils/api';
import { formatBytes, formatTimeAgo, getFileIcon } from '../utils/helpers';
import toast from 'react-hot-toast';
import { Upload, Trash2, Search, Filter, FolderOpen, X, Film, Image, Code } from 'lucide-react';

export default function MediaPage() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');

  const fetchFiles = async () => {
    try {
      const params = {};
      if (filter) params.type = filter;
      if (search) params.search = search;
      const { data } = await api.get('/media', { params });
      setFiles(data.files || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { fetchFiles(); }, [filter, search]);

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    setUploading(true);
    let success = 0;

    for (const file of acceptedFiles) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        await api.post('/media/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        success++;
      } catch (err) {
        toast.error(`Failed: ${file.name}`);
      }
    }

    if (success > 0) toast.success(`${success} file(s) uploaded`);
    setUploading(false);
    fetchFiles();
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'video/*': ['.mp4', '.webm', '.mkv'],
      'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
      'text/html': ['.html', '.htm'],
    },
  });

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      await api.delete(`/media/${id}`);
      toast.success('File deleted');
      fetchFiles();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Cannot delete');
    }
  };

  const typeIcon = (type) => {
    if (type === 'video') return <Film size={16} style={{ color: 'var(--info)' }} />;
    if (type === 'image') return <Image size={16} style={{ color: 'var(--success)' }} />;
    return <Code size={16} style={{ color: 'var(--warning)' }} />;
  };

  if (loading) return <div className="loading-page"><span className="spinner" /></div>;

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <h2>Media Library</h2>
          <div className="page-header-sub">{files.length} files</div>
        </div>
      </div>

      {/* Upload Zone */}
      <div {...getRootProps()} className={`upload-zone ${isDragActive ? 'active' : ''}`}
        style={{ marginBottom: 24 }}>
        <input {...getInputProps()} />
        <div className="upload-zone-icon"><Upload size={32} /></div>
        {uploading ? (
          <div className="upload-zone-text"><span className="spinner" style={{ display: 'inline-block', marginRight: 8 }} />Uploading...</div>
        ) : (
          <>
            <div className="upload-zone-text">
              {isDragActive ? 'Drop files here...' : 'Drag & drop files here, or click to browse'}
            </div>
            <div className="upload-zone-hint">
              Video (MP4, WebM) · Image (JPG, PNG) · HTML files
            </div>
          </>
        )}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200, position: 'relative' }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: 11, color: 'var(--text-tertiary)' }} />
          <input className="form-input" style={{ paddingLeft: 36 }}
            placeholder="Search files..."
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {[
            { v: '', l: 'All' },
            { v: 'video', l: 'Videos' },
            { v: 'image', l: 'Images' },
            { v: 'html', l: 'HTML' },
          ].map(f => (
            <button key={f.v}
              className={`btn btn-sm ${filter === f.v ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setFilter(f.v)}>
              {f.l}
            </button>
          ))}
        </div>
      </div>

      {/* File List */}
      {files.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <FolderOpen size={48} />
            <h3>No files yet</h3>
            <p>Upload videos, images, or HTML files to use in your playlists.</p>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>File</th>
                  <th>Type</th>
                  <th>Size</th>
                  <th>Uploaded</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {files.map(f => (
                  <tr key={f.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        {typeIcon(f.file_type)}
                        <span style={{ fontWeight: 500, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {f.original_name}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className="badge badge-info">{f.file_type}</span>
                    </td>
                    <td style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--text-secondary)' }}>
                      {formatBytes(f.file_size)}
                    </td>
                    <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-tertiary)' }}>
                      {formatTimeAgo(f.created_at)}
                    </td>
                    <td>
                      <button className="btn btn-ghost btn-icon btn-sm"
                        style={{ color: 'var(--danger)' }}
                        onClick={() => handleDelete(f.id, f.original_name)}>
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
